import React, { useState, useMemo, useContext, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import useModalPortalElement from '@alltrails/shared/hooks/useModalPortalElement';
import Slider from '@alltrails/shared/components/sliders/Slider';
import MoreInfoModal from '@alltrails/shared/components/modals/MoreInfoModal';
import User from '@alltrails/shared/types/User';
import { logDistanceAwaySliderTapped } from '@alltrails/analytics/amplitudeFunctions';
import { formattedStepForIndex, getSliderValues } from 'utils/slider';
import { FilterConfigSlider } from 'types/Search';
import { SearchFiltersUtil } from 'utils/search_filters_util';
import { GeoLocationContext } from 'components/explore/GeoLocationProvider';
import useCurrentGeoLocation from 'hooks/useCurrentGeoLocation';
import geoLocationOptions from 'utils/constants/GeoLocationOptions';
import { wrapUrlSafe } from 'utils/language_support_util';
import * as styles from './styles/styles.module.scss';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';

const messages = defineMessages({
  permissionTitle: {
    defaultMessage: 'Allow access, then refresh'
  },
  permissionsSubtext: {
    defaultMessage:
      'This feature shows trails based on their proximity to you. To use it, we’ll need access to your current location. Please update your browser settings, then refresh this page. {LearnHowLink}'
  },
  learnHow: {
    defaultMessage: 'Learn how'
  },
  learnHowUrl: {
    defaultMessage: 'https://support.alltrails.com/hc/en-us/articles/6352180641300'
  },
  anyValue: {
    defaultMessage: 'Any'
  }
});

export type SliderGroupProps = {
  config: FilterConfigSlider;
  handler: (changes: FilterConfigSlider) => void;
  isProFilter?: boolean;
  subText?: React.ReactNode;
  user?: User;
};

const onChange = (
  change: number,
  config: FilterConfigSlider,
  user: User,
  isProFilter: boolean,
  languageRegionCode: LanguageRegionCode
): FilterConfigSlider => {
  if (isProFilter && !user?.pro) {
    setTimeout(() => {
      const params = new URLSearchParams();
      params.set('returnTo', window.location.toString());
      window.location.assign(wrapUrlSafe(`/pro?${params.toString()}`, languageRegionCode));
    }, 750); // per product we're adding a 750 millisecond delay after using the distance away slider
  }

  const updates = getSliderValues(change, config);

  return {
    ...config,
    displayValue: updates.displayRange,
    value: updates.convertedRange,
    formattedRange: updates.formattedRange
  };
};

const SliderGroup = ({ config, handler, isProFilter, subText, user }: SliderGroupProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const { getGeoLocation } = useContext(GeoLocationContext);
  const displayFunction = SearchFiltersUtil.getFilterDisplayFunction(config?.displayFunction);
  const [hasInputChange, setHasInputChange] = useState(false);
  const [valueChangedOnce, setValueChangedOnce] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const { geoLocation } = useCurrentGeoLocation(geoLocationOptions, hasInputChange);
  const modalElement = useModalPortalElement();
  const intl = useIntl();

  const inclusiveRangeMax = config.upperBound + 1;

  const { upperBound, stepIncreaseAt, stepIncreaseBy } = config;

  const options = useMemo(
    () =>
      [...Array(inclusiveRangeMax)].map((_, index: number) => {
        if (index === upperBound) {
          return { label: intl.formatMessage(messages.anyValue) };
        }

        return {
          label: displayFunction(formattedStepForIndex({ index, stepIncreaseAt, upperBound, stepIncreaseBy }))
        };
      }),
    [inclusiveRangeMax, displayFunction, intl, upperBound, stepIncreaseAt, stepIncreaseBy]
  );

  const handleOnBeforeChange = () => {
    setValueChangedOnce(true);
    if (user && user.pro && isProFilter) {
      setShowPermissionsModal(false);
      setHasInputChange(true);
    }
  };

  const handleOnAfterChange = () => {
    if (user && user.pro && isProFilter) {
      if (geoLocation && geoLocation.isLocationBlocked) {
        setShowPermissionsModal(true);
      }
    }
  };

  useEffect(() => {
    if (valueChangedOnce) {
      // This event will be fired once per filter sheet view to reduce noise
      // user opens filters screen -> first tap = event fired -> cooldown until next filters screen open
      logDistanceAwaySliderTapped();
    }
  }, [valueChangedOnce]);

  useEffect(() => {
    if (hasInputChange) {
      getGeoLocation();
    }
  }, [hasInputChange, getGeoLocation, geoLocation]);

  return (
    <div>
      <Slider
        options={options}
        selectedIndex={config.displayValue}
        subText={subText}
        onBeforeChange={handleOnBeforeChange}
        onChange={(index: number) => handler(onChange(index, config, user, isProFilter, languageRegionCode))}
        onAfterChange={handleOnAfterChange}
      />
      {modalElement && showPermissionsModal && (
        <MoreInfoModal title={intl.formatMessage(messages.permissionTitle)} modalElement={modalElement} hideInfoButton>
          <FormattedMessage
            {...messages.permissionsSubtext}
            values={{
              LearnHowLink: (
                <a className={styles.link} href={intl.formatMessage(messages.learnHowUrl)}>
                  {intl.formatMessage(messages.learnHow)}
                </a>
              )
            }}
          />
        </MoreInfoModal>
      )}
    </div>
  );
};

export default SliderGroup;
